/**
 * BASE API SERVICE
 * Generic API wrapper for all form services
 * @version 1.0.0
 * @production-ready
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://172.30.4.101:8004/api/v1';

export interface ApiResponse<T = any> {
  ok: boolean;
  status: number;
  data: T | null;
  error: string | null;
}

/**
 * Generic fetch with timeout and error handling
 */
export const fetchApi = async <T = any>(
  endpoint: string,
  options?: RequestInit,
  timeoutMs: number = 15000
): Promise<ApiResponse<T>> => {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      signal: controller.signal
    });

    clearTimeout(timeout);

    const contentType = response.headers.get('content-type');
    const isJson = contentType?.includes('application/json');
    
    let data = null;
    if (isJson) {
      const text = await response.text();
      data = text ? JSON.parse(text) : null;
    }

    return {
      ok: response.ok,
      status: response.status,
      data,
      error: response.ok ? null : `HTTP ${response.status}`
    };
  } catch (error: any) {
    clearTimeout(timeout);
    return {
      ok: false,
      status: 0,
      data: null,
      error: error.name === 'AbortError' ? 'Request timeout' : error.message
    };
  }
};

/**
 * GET request
 */
export const get = <T = any>(endpoint: string): Promise<ApiResponse<T>> => {
  return fetchApi<T>(endpoint, { method: 'GET' });
};

/**
 * POST request
 */
export const post = <T = any>(endpoint: string, body: any): Promise<ApiResponse<T>> => {
  return fetchApi<T>(endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body)
  });
};

/**
 * PUT request
 */
export const put = <T = any>(endpoint: string, body: any): Promise<ApiResponse<T>> => {
  return fetchApi<T>(endpoint, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body)
  });
};

/**
 * DELETE request
 */
export const del = <T = any>(endpoint: string): Promise<ApiResponse<T>> => {
  return fetchApi<T>(endpoint, { method: 'DELETE' });
};

export { API_BASE_URL };
